import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PLANS, formatPrice } from '../config/plans';

/**
 * Reusable upgrade modal triggered when the backend returns 402 with a
 * `PLAN_LIMIT_REACHED` detail payload, or when a user clicks any
 * "Upgrade" button across the app.
 *
 * Props:
 *   detail:    the 402 response detail object (or null for a generic invite)
 *   onClose:   () => void
 */
export function UpgradeModal({ detail, onClose }) {
  const navigate = useNavigate();
  const currentKey = detail?.plan_key || 'starter';
  const currentIdx = PLANS.findIndex(p => p.key === currentKey);
  const next = PLANS[Math.max(currentIdx + 1, 1)] || PLANS[1];  // suggest the next tier up

  function goToPricing() {
    onClose?.();
    navigate('/pricing');
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="upgrade-modal"
      style={overlay}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div style={card}>
        <div style={iconWrap}>🚀</div>
        <h2 style={h2} data-testid="upgrade-modal-title">
          {detail ? "You've hit your plan limit" : 'Upgrade your plan'}
        </h2>
        {detail && (
          <p style={subline}>
            {detail.message || `You're using ${detail.current} of ${detail.allowed} ${detail.limit_type?.replace('max_', '').replace('_', ' ')} on the ${detail.plan_name} plan.`}
          </p>
        )}
        <div style={ladderWrap}>
          <div style={ladderCurrent}>
            <small style={ladderLabel}>Current</small>
            <strong>{(PLANS[currentIdx] || PLANS[0]).name}</strong>
          </div>
          <div style={ladderArrow}>→</div>
          <div style={ladderNext}>
            <small style={ladderLabel}>Suggested</small>
            <strong>{next.name}</strong>
            <div style={{ fontSize: '0.78rem', opacity: 0.9 }}>{formatPrice(next, 'monthly')}/mo</div>
          </div>
        </div>
        <p style={{ ...subline, fontSize: '0.82rem', marginTop: '0.8rem' }}>
          {next.key === 'enterprise'
            ? 'For unlimited capacity, talk to our team about Enterprise.'
            : `Unlock up to ${next.limits.max_children} children, ${next.limits.max_staff} staff and ${next.limits.max_centers} centre${(next.limits.max_centers || 0) === 1 ? '' : 's'}.`}
        </p>
        <div style={actions}>
          <button
            type="button"
            onClick={onClose}
            style={ghostBtn}
            data-testid="upgrade-modal-cancel"
          >Maybe later</button>
          <button
            type="button"
            onClick={goToPricing}
            style={primaryBtn}
            data-testid="upgrade-modal-cta"
          >See plans →</button>
        </div>
      </div>
    </div>
  );
}

/**
 * Helper hook-free utility: pass a thrown Error (with .response.detail)
 * or a fetch-failure into this function and it returns the structured
 * upgrade payload, or null.
 */
export function extractPlanLimitDetail(err) {
  if (!err) return null;
  // Errors from our api helpers carry the detail object as .detail (when not stringified)
  if (typeof err === 'object' && err.detail && err.detail.error_code === 'PLAN_LIMIT_REACHED') return err.detail;
  // Or as a stringified JSON in err.message (fallback)
  try {
    if (typeof err.message === 'string' && err.message.includes('PLAN_LIMIT_REACHED')) {
      const parsed = JSON.parse(err.message);
      if (parsed?.error_code === 'PLAN_LIMIT_REACHED') return parsed;
    }
  } catch { /* ignore */ }
  return null;
}

const overlay = {
  position: 'fixed', inset: 0,
  background: 'rgba(11, 30, 30, 0.55)',
  backdropFilter: 'blur(2px)',
  zIndex: 9998,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: '1.5rem',
};
const card = {
  background: '#fff', borderRadius: 16, padding: '2rem 1.6rem',
  maxWidth: 460, width: '100%',
  boxShadow: '0 30px 80px rgba(0,0,0,0.32)',
  textAlign: 'center',
};
const iconWrap = { fontSize: '2.2rem', marginBottom: '0.4rem' };
const h2 = { fontSize: '1.35rem', color: '#0b3d3a', margin: '0 0 0.5rem 0', fontWeight: 700 };
const subline = { color: '#456a67', margin: 0, fontSize: '0.92rem', lineHeight: 1.5 };
const ladderWrap = {
  display: 'flex', alignItems: 'stretch', gap: '0.6rem',
  marginTop: '1.4rem',
};
const ladderCurrent = {
  flex: 1, padding: '0.7rem', borderRadius: 10,
  background: '#f1f6f5', color: '#6c8a86',
  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.15rem',
};
const ladderNext = {
  flex: 1, padding: '0.7rem', borderRadius: 10,
  background: 'hsl(189 53% 25%)', color: '#fff',
  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.15rem',
  boxShadow: '0 8px 22px rgba(11,61,58,0.25)',
};
const ladderArrow = {
  display: 'flex', alignItems: 'center', fontSize: '1.3rem', color: '#aabfbc',
};
const ladderLabel = {
  fontSize: '0.68rem', textTransform: 'uppercase',
  letterSpacing: '0.08em', opacity: 0.85,
};
const actions = { display: 'flex', gap: '0.5rem', justifyContent: 'center', marginTop: '1.4rem', flexWrap: 'wrap' };
const primaryBtn = {
  padding: '0.6rem 1.2rem', borderRadius: 10,
  background: 'hsl(189 53% 25%)', color: '#fff', border: 'none',
  fontWeight: 700, cursor: 'pointer',
};
const ghostBtn = {
  padding: '0.6rem 1.2rem', borderRadius: 10,
  background: '#fff', color: 'hsl(189 53% 25%)',
  border: '1px solid hsl(189 53% 25%)', fontWeight: 600, cursor: 'pointer',
};

export default UpgradeModal;
